import { useState, useEffect, useRef } from 'react';
import { fetchTreeCanvasData, fetchPersonDetail } from '@/api/tree-dashboard.api';
import type { TreeCanvasData, PersonDetailData, TreeDataError } from '@/types/tree-dashboard.types';

export interface UseTreeCanvasResult {
  // Data
  canvasData: TreeCanvasData | null;
  personDetail: PersonDetailData | null;
  selectedPersonId: string | null;

  // Loading states
  isLoadingCanvas: boolean;
  isLoadingDetail: boolean;

  // Errors
  canvasError: TreeDataError | null;
  detailError: TreeDataError | null;

  // Actions
  selectPerson: (personId: string) => void;
  clearSelection: () => void;
  refresh: () => void;
}

/**
 * Production-ready hook for tree canvas data
 * Replaces useFamilyTreeData (mock version)
 *
 * @param treeId - Optional specific tree ID. If omitted, fetches user's primary tree
 * @returns UseTreeCanvasResult
 */
export function useTreeCanvas(treeId?: string): UseTreeCanvasResult {
  const [canvasData, setCanvasData] = useState<TreeCanvasData | null>(null);
  const [personDetail, setPersonDetail] = useState<PersonDetailData | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);

  const [isLoadingCanvas, setIsLoadingCanvas] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  const [canvasError, setCanvasError] = useState<TreeDataError | null>(null);
  const [detailError, setDetailError] = useState<TreeDataError | null>(null);

  // AbortController refs to cancel stale requests
  const canvasAbortControllerRef = useRef<AbortController | null>(null);
  const detailAbortControllerRef = useRef<AbortController | null>(null);

  // Load canvas data on mount
  useEffect(() => {
    loadCanvasData();

    // Cleanup: abort any pending requests when component unmounts
    return () => {
      if (canvasAbortControllerRef.current) {
        canvasAbortControllerRef.current.abort();
      }
    };
  }, [treeId]);

  // Load person detail when selection changes
  useEffect(() => {
    if (selectedPersonId) {
      loadPersonDetail(selectedPersonId);
    } else {
      // Abort any pending detail request when deselecting
      if (detailAbortControllerRef.current) {
        detailAbortControllerRef.current.abort();
      }
      setPersonDetail(null);
      setDetailError(null);
    }

    // Cleanup: abort pending detail request when selection changes
    return () => {
      if (detailAbortControllerRef.current) {
        detailAbortControllerRef.current.abort();
      }
    };
  }, [selectedPersonId]);

  /**
   * Load initial tree canvas data
   */
  async function loadCanvasData() {
    console.log('[useTreeCanvas] Loading canvas data...');

    // Abort any previous canvas request
    if (canvasAbortControllerRef.current) {
      canvasAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    canvasAbortControllerRef.current = abortController;

    setIsLoadingCanvas(true);
    setCanvasError(null);

    try {
      const data = await fetchTreeCanvasData(treeId);

      // Check if request was aborted
      if (abortController.signal.aborted) {
        console.log('[useTreeCanvas] Canvas data request was aborted');
        return;
      }

      setCanvasData(data);
      console.log('[useTreeCanvas] Canvas data loaded successfully');
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[useTreeCanvas] Canvas data request was cancelled');
        return;
      }

      console.error('[useTreeCanvas] Failed to load canvas data:', error);
      setCanvasError(error as TreeDataError);
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsLoadingCanvas(false);
      }
    }
  }

  /**
   * Load full person detail
   */
  async function loadPersonDetail(personId: string) {
    console.log('[useTreeCanvas] Loading person detail:', personId);

    // Abort any previous detail request
    if (detailAbortControllerRef.current) {
      detailAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    detailAbortControllerRef.current = abortController;

    setIsLoadingDetail(true);
    setDetailError(null);

    try {
      const data = await fetchPersonDetail(personId);

      // Check if request was aborted
      if (abortController.signal.aborted) {
        console.log('[useTreeCanvas] Person detail request was aborted');
        return;
      }

      setPersonDetail(data);
      console.log('[useTreeCanvas] Person detail loaded successfully');
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[useTreeCanvas] Person detail request was cancelled');
        return;
      }

      console.error('[useTreeCanvas] Failed to load person detail:', error);
      setDetailError(error as TreeDataError);
      setPersonDetail(null);
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsLoadingDetail(false);
      }
    }
  }

  /**
   * Select a person node (toggle behavior: clicking same person deselects)
   */
  function selectPerson(personId: string) {
    if (selectedPersonId === personId) {
      // Toggle: clicking same person closes the panel
      console.log('[useTreeCanvas] Person deselected:', personId);
      clearSelection();
    } else {
      console.log('[useTreeCanvas] Person selected:', personId);
      setSelectedPersonId(personId);
    }
  }

  /**
   * Clear selection
   */
  function clearSelection() {
    console.log('[useTreeCanvas] Selection cleared');
    setSelectedPersonId(null);
    setPersonDetail(null);
    setDetailError(null);
  }

  /**
   * Refresh canvas data
   */
  function refresh() {
    console.log('[useTreeCanvas] Refreshing canvas data...');
    loadCanvasData();
    if (selectedPersonId) {
      loadPersonDetail(selectedPersonId);
    }
  }

  return {
    canvasData,
    personDetail,
    selectedPersonId,
    isLoadingCanvas,
    isLoadingDetail,
    canvasError,
    detailError,
    selectPerson,
    clearSelection,
    refresh,
  };
}
